import {
  ExportedTree,
  ExportedTreeV,
  Tree,
  TreeNode,
  TreeNodeData
} from "@/types/tree";
import defensiveCopy from "@/util/defensive-copy";
import { isThisNodeAllowedInsideThatNode } from "./nesting";
import { CustomDocumentPostTypes, DefaultChildElement } from "@/types/elements";
import { configStore } from "@/store/store-accessor";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";

export const ROOT_ELEMENT_ID = 1;

// Like Array.reduce. Reduces the node and all of its children, all of its children's children, etc.
export function treeNodeReduce<T>(
  callback: (node: TreeNode, acc: T) => T,
  node: TreeNode,
  init: T
) {
  let acc = callback(node, init);
  node.children.forEach(childNode => {
    acc = treeNodeReduce(callback, childNode, acc);
  });
  return acc;
}

export function treeNodesOfType(node: TreeNode, slugsToMatch: string[]) {
  return treeNodeReduce(
    (node, matchingNodes) => {
      return slugsToMatch.includes(node.data.type)
        ? [...matchingNodes, node]
        : matchingNodes;
    },
    node,
    [] as TreeNode[]
  );
}

export function getIdsFromNodeAndDescendants(node: TreeNode) {
  return treeNodeReduce((node, acc: number[]) => [...acc, node.id], node, []);
}

export function areNodeTypesPresentInTree(tree: Tree, slugs: string[]) {
  return treeNodesOfType(tree.root, slugs).length > 0;
}

// Like Array.reduce. Reduces the node's parent and all of its parents' parents, etc.
export function parentNodesReduce<T>(
  callback: (node: TreeNode, acc: T) => T,
  tree: Tree,
  node: TreeNode,
  init: T
): T {
  const nodeParent: TreeNode | undefined =
    getNodeParent(tree, node.id) || undefined;

  if (nodeParent) {
    return parentNodesReduce(
      callback,
      tree,
      nodeParent,
      callback(nodeParent, init)
    );
  }

  return init;
}

export function getBlankTree(): Tree {
  const rootNode = {
    id: ROOT_ELEMENT_ID,
    data: {
      type: "root",
      properties: {}
    },
    children: []
  };

  return {
    _nextNodeId: 100,
    _lookupTable: { [`${ROOT_ELEMENT_ID}`]: rootNode },
    root: rootNode,
    status: "valid"
  };
}

// Determine whether both trees are the same.
export function treesEqual(treeA: ExportedTree, treeB: ExportedTree): boolean {
  const maybeTreeAJsonString = jsonStringifyOrUndefined(treeA);
  const maybeTreeBJsonString = jsonStringifyOrUndefined(treeB);
  return (
    maybeTreeAJsonString !== undefined &&
    maybeTreeBJsonString !== undefined &&
    maybeTreeAJsonString === maybeTreeBJsonString
  );
}

export function exportTree(tree: Tree): ExportedTree {
  return defensiveCopy({
    root: tree.root,
    _nextNodeId: tree._nextNodeId,
    status: "exported"
  });
}

export function exportNode(node: TreeNode): TreeNode {
  return defensiveCopy(node);
}

export function addParentIdToNodeAndPutNodeInLookupTable(
  tree: Tree,
  node: TreeNode,
  parentNode: TreeNode
) {
  node._parentId = parentNode.id;
  node.children.forEach(childNode => {
    addParentIdToNodeAndPutNodeInLookupTable(tree, childNode, node);
  });
  tree._lookupTable[node.id.toString()] = node;
}

export function recalculateParentIdsAndLookupTable(tree: Tree) {
  tree._lookupTable = {
    [`${ROOT_ELEMENT_ID}`]: tree.root
  };

  tree.root.children.forEach(childNode =>
    addParentIdToNodeAndPutNodeInLookupTable(tree, childNode, tree.root)
  );
}

export function createTreeFromExportedTree(exportedTree: ExportedTree): Tree {
  const tree: Tree = {
    root: exportedTree.root,
    _nextNodeId: exportedTree._nextNodeId,
    _lookupTable: {},
    status: "valid"
  };

  // We have to recalculate lookup table because we need it to contain references, not copies
  recalculateParentIdsAndLookupTable(tree);

  return tree;
}

export function getNodeIndex(tree: Tree, nodeId: number): number | false {
  const parentNode = getNodeParent(tree, nodeId);
  if (!parentNode) return false;

  const index = parentNode.children.findIndex(n => n.id === nodeId);

  return index === -1 ? false : index;
}

export function findNodeParent(
  tree: Tree,
  node: TreeNode
): TreeNode | undefined {
  return findNode(tree, node._parentId);
}

// TODO rename to getParentByNodeId
export function getNodeParent(tree: Tree, nodeId: number): TreeNode | false {
  const node = findNode(tree, nodeId);
  if (!node) return false;

  return findNodeParent(tree, node) ?? false;
}

export function findNode(tree: Tree, nodeId: number | undefined) {
  return nodeId ? tree._lookupTable[nodeId.toString()] : undefined;
}

export function isDescendant(tree: Tree, parentId: number, nodeId: number) {
  let node = getNodeParent(tree, nodeId);

  while (node != false) {
    if (node.id === parentId) return true;
    node = getNodeParent(tree, node.id);
  }

  return false;
}

/* MUTATIONS */

function _getNextNodeIdAndIncrementNextNodeId(treeToMutate: Tree) {
  const nextNodeId = treeToMutate._nextNodeId;
  treeToMutate._nextNodeId += 1;
  return nextNodeId;
}

export function createNode(treeToMutate: Tree, nodeData: TreeNodeData) {
  const newNodeId = _getNextNodeIdAndIncrementNextNodeId(treeToMutate);

  const newNode: TreeNode = {
    id: newNodeId,
    data: defensiveCopy(nodeData),
    children: []
  };

  treeToMutate._lookupTable[newNodeId.toString()] = newNode;

  return newNode;
}

export function createNodesFromSlugListAndPlace(
  tree: Tree,
  children: DefaultChildElement[] | false,
  parentNodeId: number
) {
  if (!children) {
    return;
  }

  children.forEach((child, index) => {
    const element = configStore.element(child.slug);
    const type = child.slug;
    const properties =
      child.defaultProperties || element.defaultProperties || null;

    const nestedChildren = child.children?.length
      ? child.children
      : element.defaultChildren;

    const node = createNode(tree, {
      type,
      properties
    });

    // moveNode must come first.
    // It checks for nesting rules, and they won't work if the previous parents/children aren't added until the end
    moveNode(tree, node.id, parentNodeId, index);
    createNodesFromSlugListAndPlace(tree, nestedChildren, node.id);
  });
}

export function moveNode(
  treeToMutate: Tree,
  nodeId: number,
  newParentNodeId: number,
  newNodeIndex: number,
  overrideDocumentPostType?: CustomDocumentPostTypes
) {
  const node = findNode(treeToMutate, nodeId);
  const newParentNode = findNode(treeToMutate, newParentNodeId);
  if (!node) return;
  if (!newParentNode) return;

  if (
    !isThisNodeAllowedInsideThatNode(
      treeToMutate,
      node,
      newParentNodeId,
      overrideDocumentPostType
    )
  ) {
    return;
  }
  const currentParentNode = getNodeParent(treeToMutate, nodeId);
  const currentNodeIndex = getNodeIndex(treeToMutate, nodeId);
  if (currentParentNode !== false && currentNodeIndex !== false) {
    currentParentNode.children.splice(currentNodeIndex, 1);
  }

  node._parentId = newParentNode.id;
  newParentNode.children.splice(newNodeIndex, 0, node);
}

export function deleteNode(treeToMutate: Tree, nodeId: number) {
  const parentNode = getNodeParent(treeToMutate, nodeId);
  const nodeIndex = getNodeIndex(treeToMutate, nodeId);
  if (parentNode === false) return;
  if (nodeIndex === false) return;

  parentNode.children.splice(nodeIndex, 1);
  removeNodeAndChildrenNodesFromLookupTable(treeToMutate, nodeId);
}

function removeNodeAndChildrenNodesFromLookupTable(
  treeToMutate: Tree,
  nodeId: number
) {
  const maybeNode = findNode(treeToMutate, nodeId);

  if (maybeNode && treeToMutate._lookupTable[nodeId.toString()]) {
    delete treeToMutate._lookupTable[nodeId.toString()];

    maybeNode.children.forEach(node =>
      removeNodeAndChildrenNodesFromLookupTable(treeToMutate, node.id)
    );
  }
}

/**
 * *nodeToCopy* does not need to be present in the tree -
 * for example, when adding a node from the design library,
 * the node will not be present in the tree
 */
export function copyNode(treeToMutate: Tree, nodeToCopy: TreeNode) {
  const copiedNode = createNode(treeToMutate, nodeToCopy.data);

  const recursivelyDuplicateAndPlaceChildren = (
    originalChildren: TreeNode[],
    copiedParentNode: TreeNode
  ) => {
    originalChildren.forEach(originalChildNode => {
      const copiedChild = createNode(treeToMutate, originalChildNode.data);
      moveNode(
        treeToMutate,
        copiedChild.id,
        copiedParentNode.id,
        copiedParentNode.children.length
      );

      if (originalChildNode.children.length > 0) {
        recursivelyDuplicateAndPlaceChildren(
          originalChildNode.children,
          copiedChild
        );
      }
    });
  };

  recursivelyDuplicateAndPlaceChildren(nodeToCopy.children, copiedNode);

  return copiedNode;
}

// we probably don't need this function
export function duplicateNode(treeToMutate: Tree, nodeToDuplicateId: number) {
  const parentNode = getNodeParent(treeToMutate, nodeToDuplicateId);
  const nodeIndex = getNodeIndex(treeToMutate, nodeToDuplicateId);
  const nodeToDuplicate = findNode(treeToMutate, nodeToDuplicateId);

  if (parentNode === false) return; // THROW
  if (nodeIndex === false) return; // THROW
  if (!nodeToDuplicate) return; // THROW

  const duplicatedNode = copyNode(treeToMutate, nodeToDuplicate);

  moveNode(treeToMutate, duplicatedNode.id, parentNode.id, nodeIndex + 1);

  return duplicatedNode;
}

export function countNodesOfTypeInTree(treeNode: TreeNode, nodeType: string) {
  return treeNodeReduce(
    (node, count) => {
      if (node.data.type === nodeType) {
        count++;
      }

      return count;
    },
    treeNode,
    0
  );
}
